import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { NavRoute } from "../../routes/navRoutes";
import { useCampaign } from "../../views/Campaign/CampaignContext";
import { isAdmin } from "../../utils/helpers";
import { useAuth } from "../../providers/AuthProvider";

interface SidenavItemProps {
  route: NavRoute;
  active: boolean;
  handleToggle?: () => void;
}

export default function SidenavItem({ route, active, handleToggle }: SidenavItemProps) {
  const { t } = useTranslation();
  const campaign = useCampaign();
  const { user } = useAuth();

  const isMobile = useMediaQuery("(max-width:600px)");

  return (
    <Stack className={isMobile ? "mobile" : ""}>
      <Stack
        component={Link}
        to={route.path}
        onClick={handleToggle}
        direction="row"
        spacing={2}
        alignItems="center"
        className={active ? "active" : ""}
        sx={{
          cursor: "pointer",
          bgcolor: "transparent",
          borderRadius: "4px",
          p: "11px",
          color: "caption",
          textDecoration: "none",
          ".mobile &.active": {
            borderLeft: "6px solid",
            borderColor: "primary.main",
          },
          "&:hover, &.active": {
            bgcolor: "primary.light",
            color: "primary.main",
          },
        }}
      >
        <Box component={route.icon} weight={active ? "fill" : "regular"} size={24} />
        <Typography fontWeight={500} children={t(route.pathName)} />
      </Stack>

      {route.subRoutes && (
        <Stack pt={1} pl={4}>
          {route.subRoutes.map((subRoute, index) => {
            if (!isAdmin(user) && subRoute?.hide?.(campaign)) return null;
            const subActive = window.location.pathname === subRoute.path;
            return (
              <Typography
                key={index}
                component={Link}
                onClick={handleToggle}
                to={subRoute.path}
                className={subActive ? "active" : ""}
                sx={{
                  color: "caption",
                  bgcolor: "transparent",
                  borderRadius: "4px",
                  textDecoration: "none",
                  cursor: "pointer",
                  padding: "7px 15px",
                  "&:hover, &.active": {
                    bgcolor: "primary.light",
                    color: "primary.main",
                  },
                  "&.active": {
                    borderLeft: "6px solid",
                    borderColor: "primary.main",
                    ml: "-6px",
                  },
                }}
                children={t(subRoute.pathName)}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
